import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { profileService } from '../../services/profileService';
import { SidebarProvider, useSidebar } from '../../components/ui/Sidebar';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';
import ProfileSection from './components/ProfileSection';
import SecuritySection from './components/SecuritySection';

const ProfileSettingsContent = () => {
  const { user } = useAuth();
  const { isCollapsed } = useSidebar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await profileService?.getCurrentProfile();
      setProfile(data);
    } catch (err) {
      setError(err?.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadProfile();
  }, [user, navigate, loadProfile]);

  const handleSaveProfile = async (updates) => {
    setSaving(true);
    setError(null);
    try {
      const updated = await profileService?.updateProfile(updates);
      setProfile(updated);
      setSuccessMessage('Profile updated successfully.');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <div className="mb-6">
        <Breadcrumbs items={[{ label: 'Profile Settings', path: '/profile-settings' }]} />
        <h1 className="text-3xl font-bold text-foreground mt-2">Profile Settings</h1>
        <p className="text-muted-foreground mt-1">Manage your personal information and password</p>
      </div>

      {successMessage && (
        <div className="mb-6 flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 text-green-800 rounded-lg text-sm">
          <Icon name="CheckCircle" size={14} />
          {successMessage}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error && !profile ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <Icon name="AlertCircle" size={32} className="text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
          <button
            onClick={loadProfile}
            className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="max-w-3xl space-y-6">
          {error && (
            <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              <Icon name="AlertCircle" size={14} />
              {error}
            </div>
          )}
          <ProfileSection profile={profile} onSave={handleSaveProfile} saving={saving} />
          <SecuritySection />
        </div>
      )}
    </div>
  );
};

const ProfileSettings = () => {
  return (
    <SidebarProvider>
      <Sidebar />
      <ProfileSettingsContent />
    </SidebarProvider>
  );
};

export default ProfileSettings;
